<script setup>
import ProgressStep from "@/components/ai-onboarding/partials/ProgressStep.vue";
import {Steps} from "@/composables/ai-onboarding/useSteps";
import {computed, nextTick, onMounted, ref, watch} from "vue";
import ProgressIcon from "@/components/ai-onboarding/partials/ProgressIcon.vue";
import useUser from "@/composables/useUser";
import useWebsite from "@/composables/useWebsite";

const props = defineProps({
  currentStep: {
    type: String,
    required: true,
  },
});

const { activeWebsiteIsFreeAppsumo } = useWebsite();
const {hasAccessToOptimiser} = useUser();
const progressBarSteps = computed(() => {
  const steps = Object.values(Steps.value);
  if (hasAccessToOptimiser.value === false || activeWebsiteIsFreeAppsumo.value) {
    steps.splice(-1, 1);
  }

  return steps;
});
const displayedProgressBarSteps = computed(() => progressBarSteps.value.filter(step => step.inProgressBar));
const disabled = computed(() => props.currentStep === Steps.value.HOLDING.key || props.currentStep === Steps.value.AI_AUTO_ASSIGN_HOLDING.key);

const stepsToFill = computed(() => {
  if (disabled.value) {
    return progressBarSteps.value.find(localStep => localStep.key === props.currentStep).stepsToFill;
  }

  return displayedProgressBarSteps.value.find(step => step.key === props.currentStep).stepsToFill;
});

const shouldBeCompleted = (step) => {
  // for disabled progress bar, the steps to fill should be >= to complete the previous step (accounts)
  if (disabled.value) {
    return stepsToFill.value >= step.stepsToFill;
  }

  return stepsToFill.value > step.stepsToFill;
};

// Done it this way so classes are compiled
const progressIconClassMap = computed(() => ({
  1: disabled.value ? '[&>*:nth-child(-n+1)]:tw-text-neutral' : '[&>*:nth-child(-n+1)]:tw-text-primary',
  2: disabled.value ? '[&>*:nth-child(-n+2)]:tw-text-neutral' : '[&>*:nth-child(-n+2)]:tw-text-primary',
  3: disabled.value ? '[&>*:nth-child(-n+3)]:tw-text-neutral' : '[&>*:nth-child(-n+3)]:tw-text-primary',
  4: disabled.value ? '[&>*:nth-child(-n+4)]:tw-text-neutral' : '[&>*:nth-child(-n+4)]:tw-text-primary',
  5: disabled.value ? '[&>*:nth-child(-n+5)]:tw-text-neutral' : '[&>*:nth-child(-n+5)]:tw-text-primary',
  6: disabled.value ? '[&>*:nth-child(-n+6)]:tw-text-neutral' : '[&>*:nth-child(-n+6)]:tw-text-primary',
  7: disabled.value ? '[&>*:nth-child(-n+7)]:tw-text-neutral' : '[&>*:nth-child(-n+7)]:tw-text-primary',
  8: disabled.value ? '[&>*:nth-child(-n+8)]:tw-text-neutral' : '[&>*:nth-child(-n+8)]:tw-text-primary',
}));

// escaping template wrapping
// https://stackoverflow.com/questions/71414977/refs-inside-v-for-loop-for-vue-v3-2-25-or-above
const skipUnwrap = {el: ref([])};

// used to scroll to correct element. -1 to get the index
const activeStepIndex = computed(() => stepsToFill.value - 1);

const scrollToCurrentStep = async () => {
  const activeStepElement = skipUnwrap.el.value[activeStepIndex.value];
  const element = activeStepElement.$el || activeStepElement;

  await nextTick();

  if (element instanceof HTMLElement) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }
}

watch(async () => props.currentStep, scrollToCurrentStep);

onMounted(async () => {
  scrollToCurrentStep();
});
</script>

<template>
  <div
    class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-rounded-xl tw-text-center tw-px-8 tw-pt-6 tw-w-[384px] tw-flex-shrink-0 tw-transition-all tw-duration-300 tw-h-full tw-relative"
    :class="{
      'tw-bg-white' : !disabled,
      'tw-border tw-border-grey-mid': disabled,
    }"
  >
    <div class="tw-flex tw-justify-between tw-items-center tw-w-full tw-mb-7 tw-z-[11]">
      <h2 class="tw-font-bold tw-text-grey-dark tw-text-[27px]">
        Next steps
      </h2>

      <div class="tw-flex tw-items-center tw-justify-between tw-gap-3">
        <span
          class="tw-py-1 tw-px-3 tw-rounded-3xl tw-transition-all tw-duration-300"
          :class="[
            disabled ? 'tw-bg-grey-mid tw-text-grey' : 'tw-bg-primary-100 tw-text-primary',
          ]"
        >
          {{ stepsToFill }} of {{ displayedProgressBarSteps.length }}
        </span>

        <ProgressIcon
          :key="currentStep"
          class="tw-w-14 tw-h-14 tw-transition-all tw-duration-300"
          :class="[
            disabled ? 'tw-text-grey-mid' : 'tw-text-primary-100',
            progressIconClassMap[stepsToFill],
          ]"
        />
      </div>
    </div>

    <div
      class="tw-absolute tw-w-full tw-h-[86px] tw-top-[60px] tw-to-100% tw-z-10"
      :class="{
        'tw-bg-gradient-to-t tw-from-white/0 tw-via-white tw-to-white' : !disabled,
        'tw-bg-gradient-to-t tw-from-white/0 tw-via-slate-700 tw-to-slate-700' : disabled,
      }"
    />

    <div class="tw-overflow-hidden">
      <div class="tw-flex tw-flex-col tw-gap-y-7 tw-py-8">
        <ProgressStep
          v-for="step in displayedProgressBarSteps"
          :ref="skipUnwrap.el"
          :key="step.key"
          :title="step.title"
          :description="step.description"
          :time="step.actionTime"
          :active="currentStep === step.key"
          :completed="shouldBeCompleted(step)"
        />
      </div>

      <div class="tw-h-14 tw-w-full" />
    </div>

    <div
      v-if="currentStep !== Steps.KEYWORDS.key && currentStep !== Steps.ONSITE_OPTIMIZER.key"
      class="tw-absolute tw-w-full tw-h-30 tw-bottom-0 tw-to-100% tw-rounded-b-xl"
      :class="{
        'tw-bg-gradient-to-b tw-from-white/0 tw-via-white tw-to-white' : !disabled,
        'tw-bg-gradient-to-b tw-from-white/0 tw-to-slate-700' : disabled,
      }"
    />
  </div>
</template>
